import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { checkPermission } from '@/utils/permission';
import DynamicTable from '@/components/DynamicTable';
import { ConvertCustomerScheduleType, isValidateTime, isValidDate, stripHtml } from '@/utils/helpers';
import { Button, Col, Divider, Modal, Row, Space, Tooltip } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { getSpecificCustomer } from '@/redux/actions/customerActions';
import CustomerScheduleForm from '@/routes/customer-schedule/CustomerScheduleForm';
import { getSpecificCustomerSchedule, removeCustomerSchedule } from '@/redux/actions/CustomerScheduleActions';
import CustomerScheduleDetail from '@/routes/customer-schedule/CustomerScheduleDetail';
import HtmlParser from 'react-html-parser';
import ScheduleStatusButton from '@/components/ScheduleStatusButton';
import CustomerScheduleUpdateStatusForm from '@/routes/customer-schedule/CustomerScheduleUpdateStatusForm';

const AppointmentsList = (props) => {

    const { appointments = [], customer } = props;

    const dispatch = useDispatch();
    const { id } = useParams();

    const allowCreate = checkPermission('customer_schedule', 'create');
    const allowDetail = checkPermission('customer_schedule', 'detail');
    const allowEdit = checkPermission('customer_schedule', 'update');

    const [isLoading, setIsLoading] = useState(false);
    const [isOpenScheduleForm, setIsOpenScheduleForm] = useState(false);
    const [currentSchedule, setCurrentSchedule] = useState({});
    const [isOpenDetail, setIsOpenDetail] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [openStatusForm, setOpenStatusForm] = useState(false);

    const toggleOpenForm = async (open) => {
        setIsOpenScheduleForm(open);
    };

    const handleReloadData = async () => {
        try {
            setIsLoading(true);
            await dispatch(getSpecificCustomer(id));
            console.log('Reload customer data', id);
            setIsLoading(false);
        } catch (error) {
            console.error('Failed to reload data:', error);
        }
    };

    const onDetail = async (id) => {
        setCurrentId(id);
        try {
            const res = await dispatch(getSpecificCustomerSchedule(id));
            setCurrentSchedule(res);
        } catch (error) {
            console.error('Lỗi khi tải lịch hẹn:', error);
        }
        setIsOpenDetail(true)
    };

    const onEdit = async (scheduleId) => {
        try {
            setIsLoading(true);
            const scheduleRes = await dispatch(getSpecificCustomerSchedule(scheduleId));
            setCurrentSchedule(scheduleRes);
            setIsOpenScheduleForm(true);

        } catch (error) {
            console.error('Lỗi khi mở form edit lịch hẹn:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onDelete = (id) => {
        Modal.confirm({
            title: 'Cảnh báo',
            content: 'Bạn chắc chắn muốn xoá bản ghi này?',
            okText: 'OK',
            cancelText: 'Huỷ',
            onOk: async () => {
                try {
                    setIsLoading(true);
                    await dispatch(removeCustomerSchedule([id]));
                    handleReloadData();
                } catch (error) {
                    console.error('Lỗi khi xóa lịch hẹn:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const toggleOpenUpdateStatusForm = async (id, openStatusForm) => {
        try {
            const scheduleRes = await dispatch(getSpecificCustomerSchedule(id));
            setCurrentSchedule(scheduleRes);
            setOpenStatusForm(openStatusForm);
        } catch (error) {
            console.error('Lỗi khi mở form cập nhật trạng thái lịch hẹn:', error);
        }
    }

    const columns = [
        {
            title: 'Kiểu lịch hẹn',
            dataIndex: 'name',
            key: 'name',
            width: '120px',
            render: (text, record) => {
                if (allowDetail) {
                    return (<Tooltip title="Xem chi tiết lịch hẹn" placement="right">
                        <div className='item-action-btn' onClick={() => onDetail(record.id)}>
                            <strong>{ConvertCustomerScheduleType(text)}</strong>
                        </div>
                    </Tooltip>
                    )
                } else {
                    return (
                        <strong>{ConvertCustomerScheduleType(text)}</strong>
                    )
                }
            }
        },
        {
            title: 'Ngày giờ',
            dataIndex: 'date',
            key: 'date',
            width: '150px',
            render: (text, record) => {
                return (
                    <div>{isValidDate(record.time, false)} {isValidateTime(record.time)}</div>
                )
            }
        },
        {
            title: 'Phụ trách buổi hẹn',
            dataIndex: 'doctors',
            key: 'doctors',
            width: '250px',
            render: (text, record) => {
                const doctors = record.doctors || [];
                const assistants = record.assistants || [];
                return (
                    <>
                        <div>
                            {doctors.map((staff) => (
                                <div key={staff.id}>
                                    <span style={{ color: "#999" }}>
                                        {staff.user_role?.name || ""}:{' '}
                                    </span>
                                    <span style={{ fontWeight: 600 }}>
                                        {staff.full_name}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: 3 }}>
                            {assistants.map((staff) => (
                                <div key={staff.id}>
                                    <span style={{ color: "#999" }}>
                                        {staff.user_role?.name || ""}:{' '}
                                    </span>
                                    <span style={{ fontWeight: 600 }}>
                                        {staff.full_name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )
            }
        },
        {
            title: 'Địa điểm',
            dataIndex: 'branch_names',
            key: 'branch_names',
            width: '150px',
            render: (text, record) => {
                return (
                    <div style={{ wordBreak: "break-word" }}>
                        {stripHtml(text)}
                    </div>
                )
            }
        },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            key: 'note',
            width: '250px',
            render: (text, record) => {
                return (<>{HtmlParser(text)}</>)
            }
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: '100px',
            render: (_, record) => (
                <ScheduleStatusButton
                    record={record}
                    onClick={() => toggleOpenUpdateStatusForm(record.id, true)}
                />
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            align: 'center',
            render: (_, record) => (
                <Space
                    direction="horizontal"
                    justify="center"
                    size={6}
                    align="center"
                >
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => onEdit(record.id)}
                        disabled={!allowEdit}
                    >
                        Sửa
                    </Button>
                    <Divider type="vertical" />
                    <Button
                        type="danger"
                        size="small"
                        onClick={() => onDelete(record.id)}
                        disabled={!allowDetail}
                    >
                        Xóa
                    </Button>
                </Space>
            )
        }
    ];

    const tables = <DynamicTable
        dataSource={appointments || []}
        columns={columns}
        isLoading={isLoading}
        pagination={false}
        rowSelection={false}
        moduleKey="customer_appointment_list"
        buttonStyle={{ top: 6 }}
    />

    return (
        <div>
            <div className='mt-4 mb-4'>
                <Row gutter={8}>
                    <Col>
                        <Space size={8}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setCurrentSchedule(null);      // reset data
                                    setIsOpenScheduleForm(true);
                                }}
                                disabled={!allowCreate}
                            >
                                Tạo lịch hẹn
                            </Button>
                            <Button
                                onClick={handleReloadData}
                                icon={<ReloadOutlined />}
                                loading={isLoading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>
            <div className='has-custom-column-table-btn'>{tables}</div>

            <CustomerScheduleForm
                visible={isOpenScheduleForm}
                currentItem={currentSchedule}
                currentCustomer={customer}
                reloadData={handleReloadData}
                onCancel={() => toggleOpenForm(false)}
            />
            <CustomerScheduleDetail
                visible={isOpenDetail}
                currentItem={currentSchedule}
                reloadData2={handleReloadData}
                reloadData={() => onDetail(currentId)}
                onCancel={() => setIsOpenDetail(false)}
            />
            <CustomerScheduleUpdateStatusForm
                visible={openStatusForm}
                currentItem={currentSchedule}
                reloadData={handleReloadData}
                onCancel={() => setOpenStatusForm(false)}
            />
        </div>
    );
};

export default AppointmentsList;
